import React, { forwardRef } from "react";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Box } from "@mui/material";

import { BomOutput, Version } from "fond/types";
import { calculateDelta } from "fond/utils/number";

import { BomComparisonBannerCard } from "./BomComparisonBannerCard";
import { VERSION_COLUMN_WIDTH } from "./comparisonColumnDefs";

import { BannerScrollClip, BannerSection, BannerSubtitle, BannerTitle, BannerTitleBox } from "./BomComparisonBanner.styles";

type BomComparisonBannerProps = {
  versions: Version[];
  boms: Record<string, BomOutput>;
  currency?: string;
  currentVersionId?: string;
  onOpenScenario: (versionId: string) => void;
  // Mirrors the grid's actual rendered column widths (see BomComparisonTable.tsx's syncBannerWidths),
  // so this banner's title block and version cards stay aligned with the grid's columns below.
  bannerWidths: { title: number; versions: Record<string, number> };
  isCollapsed?: boolean;
  onToggleCollapsed: () => void;
};

// Forwards its ref to the scrollable clip so BomComparisonTable can drive this banner's horizontal
// scroll position from AG-Grid's onBodyScroll event.
export const BomComparisonBanner = forwardRef<HTMLDivElement, BomComparisonBannerProps>(
  ({ versions, boms, currency, currentVersionId, onOpenScenario, bannerWidths, isCollapsed = false, onToggleCollapsed }, ref) => {
    const baselineVersion = versions[0];
    const scenarioVersions = versions.slice(1);

    if (!baselineVersion) return null;

    return (
      <BannerScrollClip ref={ref}>
        <BannerSection
          onClick={onToggleCollapsed}
          sx={{
            display: "flex",
            cursor: "pointer",
            width: bannerWidths.title + versions.reduce((sum, version) => sum + (bannerWidths.versions[version.ID] ?? VERSION_COLUMN_WIDTH), 0),
          }}
        >
          <BannerTitleBox titleWidth={bannerWidths.title} isCollapsed={isCollapsed}>
            <Box display="flex" alignItems="flex-start" gap={1}>
              {isCollapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
              <Box display="flex" flexDirection="column">
                <BannerTitle variant="h6">Scenarios — Side by Side</BannerTitle>
                {!isCollapsed && (
                  <BannerSubtitle variant="caption">
                    Comparing {scenarioVersions.length} BOM{scenarioVersions.length === 1 ? "" : "s"} against {baselineVersion.Name}
                  </BannerSubtitle>
                )}
              </Box>
            </Box>
          </BannerTitleBox>
          {versions.map((version, index) => {
            const isBaseline = version.ID === baselineVersion.ID;
            const total = boms[version.ID]?.total;
            const costDelta = isBaseline ? null : calculateDelta(total, boms[baselineVersion.ID]?.total);

            return (
              <Box
                key={version.ID}
                onClick={(e) => e.stopPropagation()}
                sx={{ width: bannerWidths.versions[version.ID] ?? VERSION_COLUMN_WIDTH, flexShrink: 0, cursor: "default" }}
              >
                <BomComparisonBannerCard
                  version={version}
                  index={index}
                  isBaseline={isBaseline}
                  isCurrent={version.ID === currentVersionId}
                  total={total}
                  costDelta={costDelta}
                  currency={currency}
                  isCollapsed={isCollapsed}
                  onOpenScenario={onOpenScenario}
                />
              </Box>
            );
          })}
        </BannerSection>
      </BannerScrollClip>
    );
  }
);

BomComparisonBanner.displayName = "BomComparisonBanner";
